import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, BookOpen, Check, Loader2, Copy, Download, MapPin, FileText, Bell } from 'lucide-react';
import { StudioOfflineBanner } from '../components/studio/StudioOfflineBanner';
import { TownAlertForm, CountryAlertForm } from '../components/studio/TownAlertForm';
import { regionSlug } from '../lib/regions';
import { CollapsibleGroup, SubHeading } from '../components/shop/CollapsibleGroup';
import { useSeo, productLd } from '../lib/seo';
import { CountryMap, useSeriesCountries } from '../components/studio/CountryMap';
import { track, trackPurchase } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `A$${Number(v).toFixed(2).replace(/\.00$/, '')}`;

const startCheckout = async (kind, slug, variant, item) => {
  if (item) track('begin_checkout', { currency: 'AUD', value: Number(item.price), items: [{ item_id: item.id, item_name: item.name, item_variant: variant, price: Number(item.price), quantity: 1 }] });
  try {
    const res = await axios.post(`${API}/guides/create-checkout`, {
      kind, slug, variant, origin_url: window.location.origin,
    });
    if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    else toast.error('Could not start checkout');
  } catch {
    toast.error('Could not start checkout. Please try again.');
  }
};

const lowestPrice = (g) => Math.min(...(g.variants || []).map((v) => v.price_aud));

export const PackCard = ({ p }) => (
  <Link to={`/shop/packs/${p.slug}`} data-testid={`pack-card-${p.slug}`} className="card-hover p-6 flex flex-col">
    <span className="eyebrow mb-3">{p.region || "Founder's"} · Collection</span>
    <h3 className="font-display text-2xl text-[#1E1622] mb-2">{p.name}</h3>
    <p className="text-sm text-[#1E1622]/60 mb-4 line-clamp-2">{p.description}</p>
    <div className="flex -space-x-3 mb-4">
      {(p.guides || []).slice(0, 5).map((g) => (
        <img key={g.product_id} src={g.cover_image_url} alt={g.town} className="w-12 h-12 rounded-lg object-cover border-2 border-white shadow" />
      ))}
    </div>
    <div className="mt-auto flex items-center justify-between">
      <span className="font-display text-2xl text-[#3B164C]">{money(p.price_aud)}</span>
      {p.savings_aud > 0 && <span className="text-xs font-semibold text-[#C5A059]">Save {money(p.savings_aud)}</span>}
    </div>
  </Link>
);

export const GuideCard = ({ g }) => (
  <Link to={`/shop/guides/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="card-hover flex flex-col overflow-hidden">
    <div className="relative h-56 overflow-hidden">
      <img src={g.cover_image_url} alt={g.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
      <span className="absolute top-4 left-4 bg-[#C5A059] text-[#2D1037] text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full">No. {g.issue_number}</span>
    </div>
    <div className="p-6 flex flex-col flex-grow">
      <span className="eyebrow mb-2">{g.region}</span>
      <h3 className="font-display text-2xl text-[#1E1622] mb-2">{g.title}</h3>
      <p className="text-sm text-[#1E1622]/60 mb-5 line-clamp-2 flex-grow">{g.short_description}</p>
      <div className="flex items-center justify-between pt-4 border-t border-[#C5A059]/20">
        <span className="text-sm text-[#1E1622]/50">from <span className="font-display text-xl text-[#3B164C]">{money(lowestPrice(g))}</span></span>
        <span className="text-[#C5A059] flex items-center gap-1 text-sm font-medium">View <ArrowRight size={16} /></span>
      </div>
    </div>
  </Link>
);

const RegionGroup = ({ region, packs, guides }) => (
  <CollapsibleGroup id={`region-${regionSlug(region)}`} title={region} image={packs[0]?.cover_image_url || guides[0]?.cover_image_url}
    meta={`${guides.length} town guide${guides.length !== 1 ? 's' : ''}${packs.length ? ' · regional pack available' : ''}`}>
    {packs.length > 0 && (
      <>
        <SubHeading>Regional pack</SubHeading>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">{packs.map((p) => <PackCard key={p.id} p={p} />)}</div>
      </>
    )}
    <SubHeading>Town guides</SubHeading>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{guides.map((g) => <GuideCard key={g.id} g={g} />)}</div>
    <Link to={`/shop/regions/${regionSlug(region)}`} data-testid={`region-page-link-${regionSlug(region)}`} className="link-gold mt-8 inline-flex">Explore the {region} page <ArrowRight size={14} /></Link>
  </CollapsibleGroup>
);

const CountryGroup = ({ country, packs, guides }) => {
  const regions = Array.from(new Set(guides.map((g) => g.region).filter(Boolean))).sort();
  return (
    <CollapsibleGroup id={`towns-country-${regionSlug(country)}`} title={country} image={guides[0]?.cover_image_url}
      meta={`${guides.length} town guide${guides.length !== 1 ? 's' : ''} · ${regions.length} region${regions.length !== 1 ? 's' : ''}`}
      action={<Link to={`/shop/countries/${regionSlug(country)}`} data-testid={`country-page-link-${regionSlug(country)}`} className="link-gold inline-flex items-center gap-1 text-sm">Explore the {country} page <ArrowRight size={14} /></Link>}>
      <div className="rounded-2xl border border-[#E6DAC5] px-5 md:px-8 bg-[#FAF7F2]/60" data-testid={`towns-country-${regionSlug(country)}-regions`}>
        {regions.map((r) => <RegionGroup key={r} region={r} packs={packs.filter((p) => p.region === r)} guides={guides.filter((g) => g.region === r)} />)}
      </div>
    </CollapsibleGroup>
  );
};

// ---------- Collection block used inside ShopPage ----------
export const StudioCollection = ({ map = null }) => {
  const [guides, setGuides] = useState([]);
  const [packs, setPacks] = useState([]);

  const load = () => {
    axios.get(`${API}/guides`).then((r) => setGuides(r.data.guides || [])).catch(() => {});
    axios.get(`${API}/studio-packs`).then((r) => setPacks(r.data.packs || [])).catch(() => {});
  };
  useEffect(load, []);

  const [showAlert, setShowAlert] = useState(false);

  if (guides.length === 0 && packs.length === 0) return <div className="mt-20"><StudioOfflineBanner onBack={load} /></div>;
  const regions = Array.from(new Set(guides.map((g) => g.region).filter(Boolean))).sort();
  const countries = Array.from(new Set(guides.map((g) => g.country || 'Italy'))).sort();

  return (
    <section className="mt-20" data-testid="studio-collection">
      <div className="text-center mb-10">
        <span className="eyebrow justify-center mb-4"><span className="w-10 h-px bg-[#C5A059]" /> Small Towns Series</span>
        <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622]">Town Guides &amp; Regional Packs</h2>
      </div>

      <StudioOfflineBanner onBack={load} />
      {map}

      <div className="flex justify-center mb-10">
        <button onClick={() => setShowAlert((v) => !v)} data-testid="town-alert-toggle" className="text-sm text-[#3B164C] hover:text-[#C5A059] inline-flex items-center gap-2 transition-colors duration-200">
          <Bell size={14} /> Get an email when new towns are published
        </button>
      </div>
      {showAlert && (
        <div className="max-w-2xl mx-auto mb-10">
          <TownAlertForm regions={regions} initialRegions={[]} />
        </div>
      )}

      <div className="bg-white rounded-3xl border border-[#E6DAC5] px-6 md:px-10" data-testid="towns-by-country">
        {countries.map((c) => <CountryGroup key={c} country={c} packs={packs.filter((p) => (p.country || 'Italy') === c && p.region)} guides={guides.filter((g) => (g.country || 'Italy') === c)} />)}
      </div>
      <div className="max-w-3xl mx-auto mt-12"><CountryAlertForm exclude={countries} /></div>
    </section>
  );
};

// ---------- Guide detail page ----------
export const GuideDetail = () => {
  const { slug } = useParams();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState([]);

  const load = () => {
    axios.get(`${API}/guides/${slug}`).then((r) => setG(r.data)).catch(() => setG(null)).finally(() => setLoading(false));
    axios.get(`${API}/studio-packs`).then((r) => setPacks(r.data.packs || [])).catch(() => {});
  };
  // eslint-disable-next-line
  useEffect(load, [slug]);
  useSeo(g && {
    title: g.seo_title || (g.title.includes(g.region || '~') ? g.title : `${g.title}${g.region ? `, ${g.region}` : ''}: Small Towns Series Guide`), description: g.short_description || g.subtitle, image: g.cover_image_url, type: 'product',
    jsonLd: productLd({ name: g.title, description: g.short_description || g.subtitle, image: g.cover_image_url, sku: g.id, offers: (g.variants || []).map((v) => ({ name: v.name, price: v.price_aud })) }),
  }, [g?.id]);
  useEffect(() => { if (g) track('view_item', { currency: 'AUD', value: lowestPrice(g), items: [{ item_id: g.id, item_name: g.title, item_category: g.region, price: lowestPrice(g) }] }); }, [g?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!g) return <div className="min-h-screen pt-32 text-center container-custom"><StudioOfflineBanner onBack={load} /><p className="text-[#1E1622]/60">Guide not found.</p><Link to="/shop" className="link-gold mt-4 inline-block">Back to shop</Link></div>;

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="guide-detail">
      <div className="container-custom">
        <div className="mb-8"><Link to="/shop" data-testid="guide-back-to-shop" className="link-gold"><ArrowLeft size={18} /> Back to shop</Link></div>
        <StudioOfflineBanner onBack={load} />
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="relative">
            <img src={g.cover_image_url} alt={g.title} className="w-full rounded-2xl shadow-[0_30px_60px_rgba(45,16,55,0.18)]" />
          </div>
          <div>
            <span className="eyebrow mb-3"><span className="w-10 h-px bg-[#C5A059]" /> <Link to={`/shop/regions/${regionSlug(g.region)}`} data-testid="guide-region-link" className="hover:text-[#3B164C] underline-offset-4 hover:underline">{g.region}</Link> · No. {g.issue_number}</span>
            <h1 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] mb-2">{g.title}</h1>
            {g.subtitle && <p className="text-xl text-[#1E1622]/70 font-light mb-4">{g.subtitle}</p>}
            <p className="text-[#1E1622]/70 leading-relaxed mb-6" data-testid="guide-long-description"><RichText text={g.long_description || g.short_description} /></p>

            {g.bullets?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#1E1622] mb-3">Inside the guide</h3>
                <ul className="space-y-2">
                  {g.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1E1622]/80"><Check size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" /> {b}</li>
                  ))}
                </ul>
              </div>
            )}

            {g.chapters?.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold text-[#1E1622] mb-3">Chapters</h3>
                <ol className="text-sm text-[#1E1622]/70 space-y-1 list-decimal list-inside">
                  {g.chapters.map((c, i) => <li key={i}>{typeof c === 'string' ? c : c.title}</li>)}
                </ol>
              </div>
            )}

            <div className="space-y-3 mb-6">
              {(g.variants || []).map((v) => (
                <div key={v.key} className="flex items-center justify-between bg-[#F5EFE9] rounded-xl p-4">
                  <div>
                    <p className="font-medium text-[#1E1622]">{v.name}</p>
                    <p className="font-display text-xl text-[#3B164C]">{money(v.price_aud)}</p>
                  </div>
                  <button onClick={() => startCheckout('guide', g.slug, v.key, { id: g.id, name: g.title, price: v.price_aud })} data-testid={`buy-${g.slug}-${v.key}`} className="btn-gold text-sm">Buy {v.key === 'bundle' ? 'bundle' : 'PDF'}</button>
                </div>
              ))}
            </div>

            <PackUpsell pack={packs.find((p) => (p.guides || []).some((x) => String(x.product_id) === String(g.id)))} />

            <div className="flex flex-wrap items-center gap-5 text-sm">
              {g.sample_url && <a href={g.sample_url} target="_blank" rel="noopener noreferrer" data-testid="sample-link" className="link-gold"><FileText size={16} /> Read Chapter I free</a>}
              {g.changelog_url && <a href={g.changelog_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E1622]/50 hover:text-[#C5A059]">What's new in this edition →</a>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PackUpsell = ({ pack }) => {
  if (!pack) return null;
  return (
    <Link to={`/shop/packs/${pack.slug}`} data-testid={`pack-upsell-${pack.slug}`}
      className="group block mb-6 rounded-2xl bg-[#2D1037] text-[#FAF7F2] p-5 border border-[#C5A059]/40 transition-transform duration-300 hover:-translate-y-0.5">
      <div className="flex items-center gap-4">
        <div className="flex -space-x-3 flex-shrink-0">
          {(pack.guides || []).slice(0, 4).map((x) => (
            <img key={x.product_id} src={x.cover_image_url} alt={x.town} className="w-10 h-12 rounded-md object-cover border-2 border-[#2D1037]" />
          ))}
        </div>
        <div className="flex-grow">
          <p className="font-display text-xl leading-tight">Get all {pack.guide_count} {pack.region} towns for {money(pack.price_aud)}</p>
          <p className="text-xs text-[#E5C178] mt-1">
            {pack.savings_aud > 0 && <>Save {money(pack.savings_aud)} vs buying separately</>}
            {pack.includes_toolkit && <> · offline toolkits included</>}
          </p>
        </div>
        <ArrowRight size={18} className="text-[#C5A059] transition-transform duration-300 group-hover:translate-x-1" />
      </div>
    </Link>
  );
};

// ---------- Pack detail page ----------
export const PackDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    axios.get(`${API}/studio-packs/${slug}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
  };
  // eslint-disable-next-line
  useEffect(load, [slug]);
  useSeo(p && {
    title: `${p.name}: ${p.guide_count} Small Town Guides${p.region ? ` in ${p.region}` : ` across ${p.country || 'Italy'}`}`, description: p.description, image: p.cover_image_url || p.guides?.[0]?.cover_image_url, type: 'product',
    jsonLd: productLd({ name: p.name, description: p.description, image: p.cover_image_url, sku: p.id, offers: [{ name: p.name, price: p.price_aud }] }),
  }, [p?.id]);
  useEffect(() => { if (p) track('view_item', { currency: 'AUD', value: Number(p.price_aud), items: [{ item_id: p.id, item_name: p.name, item_category: p.region, price: Number(p.price_aud) }] }); }, [p?.id]);

  if (loading) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;
  if (!p) return <div className="min-h-screen pt-32 text-center"><p className="text-[#1E1622]/60">Pack not found.</p><Link to="/shop" className="link-gold mt-4 inline-block">Back to shop</Link></div>;

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="pack-detail">
      <div className="container-custom max-w-4xl">
        <div className="mb-8"><Link to="/shop" data-testid="pack-back-to-shop" className="link-gold"><ArrowLeft size={18} /> Back to shop</Link></div>
        <StudioOfflineBanner onBack={load} />
        <span className="eyebrow mb-3"><span className="w-10 h-px bg-[#C5A059]" /> {p.region ? <Link to={`/shop/regions/${regionSlug(p.region)}`} data-testid="pack-region-link" className="hover:text-[#3B164C] hover:underline underline-offset-4">{p.region}</Link> : "Founder's Collection"} · {p.guide_count} guides</span>
        <h1 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] mb-3">{p.name}</h1>
        <p className="text-[#1E1622]/70 leading-relaxed mb-8">{p.description}</p>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          {(p.guides || []).map((g) => (
            <Link to={`/shop/guides/${g.slug}`} key={g.product_id} className="card-hover overflow-hidden">
              <img src={g.cover_image_url} alt={g.town} className="w-full h-40 object-cover" />
              <div className="p-3">
                <p className="font-medium text-[#1E1622] text-sm">{g.town}</p>
                <p className="text-xs text-[#1E1622]/50">No. {g.issue_number} · {g.edition}</p>
              </div>
            </Link>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 bg-[#F5EFE9] rounded-2xl p-6">
          <div>
            <p className="font-display text-3xl text-[#3B164C]">{money(p.price_aud)}</p>
            {p.savings_aud > 0 && <p className="text-sm text-[#C5A059] font-semibold">Save {money(p.savings_aud)} vs buying separately</p>}
          </div>
          <button onClick={() => startCheckout('pack', p.slug, undefined, { id: p.id, name: p.name, price: p.price_aud })} data-testid={`buy-pack-${p.slug}`} className="btn-gold">Buy the collection</button>
        </div>
      </div>
    </div>
  );
};

// ---------- Thank-you page for studio orders ----------
export const LinkButtons = ({ pdf_url, app_url }) => (
  <div className="flex flex-wrap gap-3">
    {pdf_url && <a href={pdf_url} target="_blank" rel="noopener noreferrer" className="btn-gold text-sm inline-flex items-center gap-2"><Download size={16} /> Download PDF</a>}
    {app_url && <a href={app_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm inline-flex items-center gap-2"><Download size={16} /> Offline toolkit</a>}
  </div>
);

export const GuideSuccess = () => {
  const [sp] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');

  const load = async (refresh = false) => {
    const sid = sp.get('session_id');
    if (!sid) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/payments/stripe/success`, { params: { session_id: sid, refresh } });
      setData(r.data.download || null);
      setEmail(r.data.customer_email || '');
      if (r.data.success) trackPurchase(r.data.purchase);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copyPw = (pw) => { navigator.clipboard.writeText(pw); toast.success('Password copied'); };

  if (loading) return <div className="min-h-screen pt-32 flex justify-center"><Loader2 className="animate-spin text-[#3B164C]" size={40} /></div>;

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="guide-success">
      <div className="container-custom max-w-2xl">
        <div className="bg-white rounded-3xl border border-[#E6DAC5] p-8 md:p-12">
          <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 flex items-center justify-center mb-6"><BookOpen size={30} className="text-[#C5A059]" /></div>
          <h1 className="font-display text-4xl font-medium text-[#1E1622] mb-3">Grazie! Your guide is ready.</h1>
          <p className="text-[#1E1622]/70 mb-8">We've also emailed your download links{email && <> to <span className="font-medium">{email}</span></>}.</p>

          {!data ? (
            <p className="text-[#1E1622]/60">Your links are being prepared — please check your email in a few minutes.</p>
          ) : data.guides ? (
            <div className="space-y-5" data-testid="pack-downloads">
              {data.guides.map((g, i) => (
                <div key={i} className="bg-[#F5EFE9] rounded-2xl p-5">
                  <p className="font-medium text-[#1E1622] mb-3">{g.town} <span className="text-[#1E1622]/50 text-sm">· {g.edition}</span></p>
                  <LinkButtons pdf_url={g.pdf_url} app_url={g.app_url} />
                </div>
              ))}
              {data.pdf_password && <PwBox pw={data.pdf_password} onCopy={copyPw} />}
            </div>
          ) : (
            <div className="space-y-4" data-testid="guide-downloads">
              <LinkButtons pdf_url={data.pdf_url} app_url={data.app_url} />
              {data.pdf_password && <PwBox pw={data.pdf_password} onCopy={copyPw} />}
            </div>
          )}

          {data && (
            <p className="text-xs text-[#1E1622]/50 mt-6">
              {data.expires_at && <>Links expire on {new Date(data.expires_at).toLocaleString()}. </>}
              {data.max_downloads && <>Up to {data.max_downloads} downloads.</>}
            </p>
          )}

          <div className="mt-8 flex gap-4">
            <Link to="/shop" className="btn-secondary text-sm">Continue shopping</Link>
            <Link to="/orders" data-testid="success-my-orders-link" className="text-sm link-gold">My orders</Link>
            {sp.get('session_id') && <button onClick={() => { setLoading(true); load(true); }} data-testid="get-fresh-links" className="text-sm link-gold">Get fresh links</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

const RichText = ({ text = '' }) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
  part.startsWith('**') && part.endsWith('**') ? <strong key={i} className="font-semibold text-[#1E1622]">{part.slice(2, -2)}</strong> : part
));

export const PwBox = ({ pw, onCopy }) => (
  <div className="flex items-center justify-between bg-[#2D1037] text-[#FAF7F2] rounded-xl px-4 py-3" data-testid="pdf-password">
    <span className="text-sm">PDF password: <span className="font-mono font-semibold text-[#E5C178]">{pw}</span></span>
    <button onClick={() => onCopy(pw)} data-testid="copy-password" className="text-[#C5A059] hover:text-[#E5C178]"><Copy size={16} /></button>
  </div>
);

// ---------- Homepage map section ----------
const SeriesCountryAlert = () => <CountryAlertForm exclude={useSeriesCountries()} />;

export const STUDIO_MAP_EMBED = 'https://smalltownsitaly.theexpatstudio.com/api/public/map/embed';

export const GuidesMapSection = () => (
  <section className="pt-20 md:pt-28 bg-[#FAF7F2]" data-testid="guides-map-section">
    <div className="container-custom text-center max-w-2xl mx-auto mb-10">
      <span className="eyebrow justify-center mb-4"><span className="w-10 h-px bg-[#C5A059]" /> Explore</span>
      <h2 className="font-display text-4xl md:text-5xl font-medium text-[#1E1622] mb-4">Small Towns Series, on the map</h2>
      <p className="text-[#1E1622]/60 font-light mb-6" data-testid="guides-map-intro">Every pin is a town with its own guide: tap one to open it.</p>
      <Link to="/shop#small-towns-series" data-testid="guides-map-browse-btn" className="btn-gold inline-flex items-center gap-2"><MapPin size={18} /> Browse the guides</Link>
    </div>
    <CountryMap testPrefix="guides-map" />
    <div className="container-custom max-w-3xl mx-auto py-14"><SeriesCountryAlert /></div>
  </section>
);
